/**
 * 矢量填充/描边取色（形状图层与文字共用，DESIGN.md §6.3 补充）。
 *
 * 鸭子类型，同 engine/effects/resolve.ts 的做法：不 import document/types
 * （engine 不能反向依赖上层），这里自己声明一份结构等价的 `VectorFillLike`。
 *
 * 渐变几何固定死：从传入 bounds 的左边到右边的水平线性渐变，复用
 * engine/fill/gradient.ts 的取色公式（跟渐变工具/GPU 着色器同一份数学，
 * 不会出现"形状渐变和画笔渐变看起来不一样"的割裂）。
 */
import { parseColor } from '../effects/resolve';
import { gradientColorAt, gradientT, type GradientSpec } from './gradient';

export type VectorFillLike =
  | { readonly type: 'solid'; readonly color: string; readonly alpha?: number }
  | { readonly type: 'gradient'; readonly stops: readonly { readonly offset: number; readonly color: string }[] };

export interface FillBounds {
  readonly x: number;
  readonly y: number;
  readonly width: number;
  readonly height: number;
}

/**
 * (x, y) 处的取色，sRGB 0–255 + alpha 0–1。x/y 与 bounds 同一坐标空间。
 * 渐变本身不带透明度（色标的不透明度是渐变编辑器里独立的滑杆，跟这里的
 * 矢量填充层级不是一回事，暂不打通）——alpha 恒为 1，只有纯色会 < 1。
 */
export function sampleVectorFill(
  fill: VectorFillLike,
  bounds: FillBounds,
  x: number,
  y: number,
): readonly [number, number, number, number] {
  if (fill.type === 'solid') {
    const [r, g, b] = parseColor(fill.color);
    return [r, g, b, fill.alpha ?? 1];
  }
  if (fill.stops.length === 0) return [0, 0, 0, 1];
  const spec: GradientSpec = {
    kind: 'linear',
    from: { x: bounds.x, y: bounds.y + bounds.height / 2 },
    to: { x: bounds.x + bounds.width, y: bounds.y + bounds.height / 2 },
    space: 'srgb',
    stops: fill.stops.map((s) => {
      const [r, g, b] = parseColor(s.color);
      return { offset: s.offset, color: [r, g, b, 255] as [number, number, number, number] };
    }),
  };
  const [r, g, b] = gradientColorAt(spec, gradientT(spec, x + 0.5, y + 0.5));
  return [r, g, b, 1];
}
